import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Image, X, Upload, Trash2, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { DatePicker } from "./SimplePickers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const { member, isAdmin, getToken } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAlbumForm, setShowAlbumForm] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [showDeleteMode, setShowDeleteMode] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [albumForm, setAlbumForm] = useState({
    title: "",
    description: "",
    date: "",
    allow_member_upload: false
  });
  const fileInputRef = useRef(null);
  const multiFileInputRef = useRef(null);

  const canCreate = member && (member.role === "editor" || member.role === "admin");
  const canUploadToAlbum = (album) => {
    if (!member || member.role === "pending") return false;
    if (member.role === "editor" || member.role === "admin") return true;
    return album?.allow_member_upload && member.role === "member";
  };

  // Demo albums for display when no real albums exist
  const demoAlbums = [
    {
      album_id: "demo_1",
      title: "Konfirmationskørsel",
      description: "En fantastisk dag hvor vi eskorterede konfirmanden til kirken med stil. 15 motorcykler i formation gennem byen.",
      date: "2025-05-15T12:00:00.000Z",
      cover_image: null,
      cover_url: "https://images.unsplash.com/photo-1558981285-6f0c94958bb6?w=800",
      images: [
        { image_id: "d1_1", url: "https://images.unsplash.com/photo-1558981285-6f0c94958bb6?w=800" },
        { image_id: "d1_2", url: "https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=800" },
        { image_id: "d1_3", url: "https://images.unsplash.com/photo-1609630875171-b1321377ee65?w=800" },
      ],
      isDemo: true
    },
    {
      album_id: "demo_2",
      title: "Klubtur til Lolland",
      description: "Weekendtur med overnatning. Vi kørte langs kysten og nød det gode vejr med frokost ved Maribo Sø.",
      date: "2025-06-22T10:00:00.000Z",
      cover_image: null,
      cover_url: "https://images.unsplash.com/photo-1449426468159-d96dbf08f19f?w=800",
      images: [
        { image_id: "d2_1", url: "https://images.unsplash.com/photo-1449426468159-d96dbf08f19f?w=800" },
        { image_id: "d2_2", url: "https://images.unsplash.com/photo-1558980664-769d59546b3d?w=800" },
        { image_id: "d2_3", url: "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=800" },
        { image_id: "d2_4", url: "https://images.unsplash.com/photo-1525160354320-d8e92641c563?w=800" },
      ],
      isDemo: true
    },
    {
      album_id: "demo_3",
      title: "Velgørenhedsevent",
      description: "Årlig indsamling til lokalsamfundet. Tak til alle der deltog og bidrog til den gode sag!",
      date: "2025-08-10T09:00:00.000Z",
      cover_image: null,
      cover_url: "https://images.unsplash.com/photo-1558981033-0f0309284409?w=800",
      images: [
        { image_id: "d3_1", url: "https://images.unsplash.com/photo-1558981033-0f0309284409?w=800" },
        { image_id: "d3_2", url: "https://images.unsplash.com/photo-1590496793929-36417d3117de?w=800" },
        { image_id: "d3_3", url: "https://images.unsplash.com/photo-1558980664-2506fca6bfc2?w=800" },
      ],
      isDemo: true
    },
    {
      album_id: "demo_4",
      title: "Fødselsdag i klubhuset",
      description: "Vores kære medlem Michael fyldte 50 år. Grillaften med musik og godt selskab.",
      date: "2025-09-05T18:00:00.000Z",
      cover_image: null,
      cover_url: "https://images.unsplash.com/photo-1593241538053-b32565a5465c?w=800",
      images: [
        { image_id: "d4_1", url: "https://images.unsplash.com/photo-1593241538053-b32565a5465c?w=800" },
        { image_id: "d4_2", url: "https://images.unsplash.com/photo-1546801375-cb25ea841643?w=800" },
      ],
      isDemo: true
    },
  ];

  // Use demo albums if no real albums exist
  const displayAlbums = albums.length > 0 ? albums : demoAlbums;

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      const res = await axios.get(`${API}/albums`);
      setAlbums(res.data);
    } catch (err) {
      console.error("Kunne ikke hente albums");
    } finally {
      setLoading(false);
    }
  };

  const [savingAlbum, setSavingAlbum] = useState(false);
  const [savedAlbum, setSavedAlbum] = useState(false);

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    setSavingAlbum(true);
    try {
      if (editingAlbum) {
        await axios.put(`${API}/albums/${editingAlbum.album_id}`, {
          ...albumForm,
          date: new Date(albumForm.date).toISOString()
        }, { headers: authHeaders() });
        toast.success("Album opdateret");
      } else {
        await axios.post(`${API}/albums`, {
          ...albumForm,
          date: new Date(albumForm.date).toISOString()
        }, { headers: authHeaders() });
        toast.success("Album oprettet");
      }
      setSavedAlbum(true);
      setTimeout(() => { setSavedAlbum(false); setShowAlbumForm(false); setEditingAlbum(null); }, 1200);
      setAlbumForm({ title: "", description: "", date: "", allow_member_upload: false });
      fetchAlbums();
    } catch (err) {
      toast.error("Kunne ikke gemme album");
    } finally {
      setSavingAlbum(false);
    }
  };

  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm("Er du sikker på du vil slette dette album og alle billeder?")) return;
    try {
      await axios.delete(`${API}/albums/${albumId}`, { headers: authHeaders() });
      toast.success("Album slettet");
      setSelectedAlbum(null);
      fetchAlbums();
    } catch (err) {
      toast.error("Kunne ikke slette album");
    }
  };

  const handleUploadImage = async (e, albumId) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    try {
      // Upload all selected files directly
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        await axios.post(`${API}/albums/${albumId}/images`, formData, {
          headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
        });
      }
      
      toast.success(files.length > 1 ? `${files.length} billeder uploadet` : "Billede uploadet");
      fetchAlbums();
      
      // Refresh selected album
      const res = await axios.get(`${API}/albums/${albumId}`);
      setSelectedAlbum(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fejlede");
    } finally {
      setUploading(false);
    }
    
    // Clear input so same file can be selected again
    e.target.value = '';
  };

  const handleDeleteImage = async (albumId, imageId) => {
    if (!window.confirm("Slet dette billede?")) return;
    try {
      await axios.delete(`${API}/albums/${albumId}/images/${imageId}`, { headers: authHeaders() });
      toast.success("Billede slettet");
      const res = await axios.get(`${API}/albums/${albumId}`);
      setSelectedAlbum(res.data);
      fetchAlbums();
    } catch (err) {
      toast.error("Kunne ikke slette billede");
    }
  };

  const openEditAlbum = (album) => {
    setEditingAlbum(album);
    setAlbumForm({
      title: album.title,
      description: album.description || "",
      date: new Date(album.date).toISOString().split('T')[0],
      allow_member_upload: album.allow_member_upload
    });
    setShowAlbumForm(true);
  };

  const getImageUrl = (image) => {
    if (image.url) {
      return image.url.startsWith('/api/') ? `${BACKEND_URL}${image.url}` : image.url;
    }
    return `${BACKEND_URL}/api/uploads/gallery/${image.filename}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-black" data-testid="gallery-page">
      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[400px]">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_00f587e7-4714-4720-98a9-b623d9c78756/artifacts/cbdndxgd_IMG_4043.jpeg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Gradient overlay - fades to black at bottom */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,1) 100%)'
          }}
        />
        
        {/* Hero Text - Left aligned, higher position */}
        <div className="absolute bottom-32 left-0 right-0 px-6 sm:px-12">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-5xl sm:text-6xl font-['Oswald'] font-bold text-white drop-shadow-lg mb-4">
              GALLERI
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 drop-shadow-lg max-w-xl">
              Øjeblikke fra vejen. Se billeder fra vores ture, events og fællesskab.
            </p>
          </div>
        </div>
      </div>

      {/* Albums Section - Black background */}
      <div className="bg-black px-6 sm:px-12 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header with create button */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <h2 className="text-3xl font-['Oswald'] font-bold text-white">ALBUMS</h2>
            <div className="flex gap-4">
              {canCreate && (
                <button
                  onClick={() => {
                    setEditingAlbum(null);
                    setAlbumForm({ title: "", description: "", date: new Date().toISOString().split('T')[0], allow_member_upload: false });
                    setShowAlbumForm(true);
                  }}
                  className="chrome-btn px-6 py-3 rounded-lg"
                  data-testid="create-album-btn"
                >
                  + Tilføj
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayAlbums.map((album) => (
                <div
                  key={album.album_id}
                  className="group relative overflow-hidden rounded-xl cursor-pointer bg-neutral-900 border border-neutral-800 hover:border-blue-500/50 transition-all"
                  onClick={() => setSelectedAlbum(album)}
                  data-testid={`album-card-${album.album_id}`}
                >
                  {/* Cover Image */}
                  <div className="aspect-video bg-neutral-800 overflow-hidden">
                    {album.cover_url ? (
                      <img
                        src={album.cover_url}
                        alt={album.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : album.cover_image ? (
                      <img
                        src={album.cover_image?.startsWith('/api/') ? `${BACKEND_URL}${album.cover_image}` : `${BACKEND_URL}/api/uploads/gallery/${album.cover_image}`}
                        alt={album.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600">
                        <span className="text-5xl">📷</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Album Info */}
                  <div className="p-4">
                    <h3 className="text-xl font-['Oswald'] font-bold text-white mb-1">{album.title}</h3>
                    <p className="text-blue-400 text-sm mb-2">{formatDate(album.date)}</p>
                    {album.description && (
                      <p className="text-gray-400 text-sm line-clamp-2">{album.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-gray-600 text-sm">
                        {album.images?.length || 0} billeder
                      </p>
                      {(member?.role === "admin" || member?.role === "editor") && !album.isDemo && (
                        <span className="text-blue-400 text-sm flex items-center gap-1">
                          <Plus size={14} />
                          Tilføj flere
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Delete button on hover for admin/editor */}
                  {(member?.role === "admin" || member?.role === "editor") && !album.isDemo && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAlbum(album.album_id);
                      }}
                      className="absolute top-2 right-2 bg-red-600/90 hover:bg-red-500 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all flex items-center gap-1"
                      data-testid={`delete-album-card-${album.album_id}`}
                    >
                      <Trash2 size={16} />
                      <span className="text-sm">Slet</span>
                    </button>
                  )}
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Album Detail Modal */}
      {selectedAlbum && (
        <div className="fixed inset-0 bg-black/95 z-50 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-8">
              <div>
                <button
                  onClick={() => setSelectedAlbum(null)}
                  className="text-gray-400 hover:text-white mb-4 flex items-center gap-2"
                >
                  ← Tilbage til albums
                </button>
                <h2 className="text-4xl font-['Oswald'] font-bold text-white">{selectedAlbum.title}</h2>
                <p className="text-blue-400 mt-1">{formatDate(selectedAlbum.date)}</p>
                {selectedAlbum.description && (
                  <p className="text-gray-400 mt-2 max-w-2xl">{selectedAlbum.description}</p>
                )}
              </div>
              
              <div className="flex gap-3 flex-wrap">
                {canCreate && !selectedAlbum.isDemo && (
                  <button
                    onClick={() => openEditAlbum(selectedAlbum)}
                    className="ghost-blue-btn px-4 py-2 rounded-lg"
                  >
                    Rediger
                  </button>
                )}
                {member?.role === "admin" && !selectedAlbum.isDemo && selectedAlbum.images?.length > 0 && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Er du sikker på du vil slette ALLE ${selectedAlbum.images.length} billeder i dette album?`)) return;
                      for (const image of selectedAlbum.images) {
                        try {
                          await axios.delete(`${API}/albums/${selectedAlbum.album_id}/images/${image.image_id}`, { headers: authHeaders() });
                        } catch (err) {
                          console.error("Kunne ikke slette billede:", image.image_id);
                        }
                      }
                      toast.success("Alle billeder slettet");
                      const res = await axios.get(`${API}/albums/${selectedAlbum.album_id}`);
                      setSelectedAlbum(res.data);
                      fetchAlbums();
                    }}
                    className="bg-red-600/20 text-red-400 hover:bg-red-600/30 px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    Slet alle billeder
                  </button>
                )}
                {member?.role === "admin" && !selectedAlbum.isDemo && (
                  <button
                    onClick={() => handleDeleteAlbum(selectedAlbum.album_id)}
                    className="text-red-400 hover:text-red-300 px-4 py-2"
                  >
                    Slet album
                  </button>
                )}
              </div>
            </div>

            {/* Upload Section - Expandable */}
            {canUploadToAlbum(selectedAlbum) && !selectedAlbum.isDemo && (
              <div className="mb-6">
                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={() => {
                      setShowUploadSection(!showUploadSection);
                      setShowDeleteMode(false);
                    }}
                    className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all ${
                      showUploadSection 
                        ? 'bg-green-600 text-white' 
                        : 'chrome-btn'
                    }`}
                    data-testid="toggle-upload-section-btn"
                  >
                    <Plus size={20} />
                    Tilføj billeder
                  </button>
                  
                  {(member?.role === "admin" || member?.role === "editor") && selectedAlbum.images?.length > 0 && (
                    <button
                      onClick={() => {
                        setShowDeleteMode(!showDeleteMode);
                        setShowUploadSection(false);
                      }}
                      className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all ${
                        showDeleteMode 
                          ? 'bg-red-600 text-white' 
                          : 'bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-500/30'
                      }`}
                      data-testid="toggle-delete-mode-btn"
                    >
                      <Trash2 size={20} />
                      {showDeleteMode ? 'Afslut sletning' : 'Slet billeder'}
                    </button>
                  )}
                </div>
                
                {/* Delete Mode Instructions */}
                {showDeleteMode && (
                  <div className="mt-4 bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                    <h3 className="text-red-400 font-bold mb-2 flex items-center gap-2">
                      <Trash2 size={18} />
                      SLET-TILSTAND AKTIV
                    </h3>
                    <p className="text-gray-300 text-sm mb-3">
                      Klik på de billeder du vil slette. Der kommer en bekræftelse for hvert billede.
                    </p>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Er du sikker på du vil slette ALLE ${selectedAlbum.images.length} billeder i dette album?`)) return;
                        let deleted = 0;
                        for (const image of selectedAlbum.images) {
                          try {
                            await axios.delete(`${API}/albums/${selectedAlbum.album_id}/images/${image.image_id}`, { headers: authHeaders() });
                            deleted++;
                          } catch (err) {
                            console.error("Kunne ikke slette:", image.image_id);
                          }
                        }
                        toast.success(`${deleted} billeder slettet`);
                        const res = await axios.get(`${API}/albums/${selectedAlbum.album_id}`);
                        setSelectedAlbum(res.data);
                        setShowDeleteMode(false);
                        fetchAlbums();
                      }}
                      className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                    >
                      <Trash2 size={16} />
                      Slet ALLE billeder ({selectedAlbum.images?.length})
                    </button>
                  </div>
                )}
                
                {showUploadSection && (
                  <div className="mt-4 bg-neutral-900/80 border border-neutral-700 rounded-xl p-6">
                    <h3 className="text-xl font-['Oswald'] font-bold text-white mb-4">TILFØJ BILLEDER</h3>
                    
                    {/* Optional title for the upload batch */}
                    <div className="mb-4">
                      <label className="block text-gray-300 mb-2">Overskrift (valgfri)</label>
                      <input
                        type="text"
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                        className="mc-input w-full"
                        placeholder="F.eks. 'Billeder fra ankomsten'"
                        data-testid="upload-title-input"
                      />
                      <p className="text-gray-500 text-sm mt-1">Tilføj en overskrift der beskriver billederne</p>
                    </div>
                    
                    {/* File upload area */}
                    <div 
                      className="border-2 border-dashed border-neutral-600 rounded-xl p-8 text-center hover:border-blue-500 transition-colors cursor-pointer"
                      onClick={() => multiFileInputRef.current?.click()}
                    >
                      <input
                        ref={multiFileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          if (files.length === 0) return;
                          
                          setUploading(true);
                          let successCount = 0;
                          
                          for (const file of files) {
                            const formData = new FormData();
                            formData.append("file", file);
                            
                            try {
                              await axios.post(`${API}/albums/${selectedAlbum.album_id}/images`, formData, {
                                headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
                              });
                              successCount++;
                            } catch (err) {
                              console.error("Upload fejlede for:", file.name);
                            }
                          }
                          
                          if (successCount > 0) {
                            toast.success(`${successCount} billede${successCount > 1 ? 'r' : ''} uploadet`);
                            fetchAlbums();
                            const res = await axios.get(`${API}/albums/${selectedAlbum.album_id}`);
                            setSelectedAlbum(res.data);
                          }
                          
                          setUploading(false);
                          setUploadTitle("");
                          e.target.value = "";
                        }}
                        className="hidden"
                      />
                      
                      {uploading ? (
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          <p className="text-white">Uploader billeder...</p>
                        </div>
                      ) : (
                        <>
                          <div className="text-5xl mb-4">📷</div>
                          <p className="text-white text-lg mb-2">Klik for at vælge billeder</p>
                          <p className="text-gray-400 text-sm">eller træk og slip filer her</p>
                          <p className="text-gray-500 text-xs mt-2">Du kan vælge flere billeder ad gangen</p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Demo badge */}
            {selectedAlbum.isDemo && (
              <div className="mb-6 bg-blue-600/20 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-400 text-sm">
                  📸 Dette er et eksempel-album. Log ind som editor eller admin for at oprette dine egne albums.
                </p>
              </div>
            )}

            {/* Images Grid */}
            {selectedAlbum.images?.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-500 text-xl mb-4">Ingen billeder i dette album</p>
                {canUploadToAlbum(selectedAlbum) && !selectedAlbum.isDemo && (
                  <button
                    onClick={() => setShowUploadSection(true)}
                    className="chrome-btn px-6 py-3 rounded-lg"
                  >
                    Upload det første billede
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {selectedAlbum.images?.map((image) => (
                  <div
                    key={image.image_id}
                    className={`group relative aspect-square overflow-hidden rounded-lg cursor-pointer bg-neutral-900 ${
                      showDeleteMode ? 'ring-2 ring-red-500/50' : ''
                    }`}
                    onClick={() => {
                      if (showDeleteMode && (member?.role === "admin" || member?.role === "editor")) {
                        handleDeleteImage(selectedAlbum.album_id, image.image_id);
                      } else {
                        setSelectedImage(image);
                      }
                    }}
                  >
                    <img
                      src={getImageUrl(image)}
                      alt=""
                      className={`w-full h-full object-cover transition-transform ${showDeleteMode ? '' : 'group-hover:scale-105'}`}
                    />
                    
                    {/* Normal hover overlay */}
                    {!showDeleteMode && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                    
                    {/* Delete mode overlay - always visible */}
                    {showDeleteMode && (
                      <div className="absolute inset-0 bg-red-900/60 flex flex-col items-center justify-center">
                        <Trash2 size={32} className="text-white mb-2" />
                        <span className="text-white text-sm font-medium">Klik for at slette</span>
                      </div>
                    )}
                    
                    {/* Delete button on hover (when not in delete mode) */}
                    {!showDeleteMode && (member?.role === "admin" || member?.role === "editor") && !selectedAlbum.isDemo && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(selectedAlbum.album_id, image.image_id);
                        }}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-500 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all flex items-center gap-1"
                        data-testid={`delete-image-${image.image_id}`}
                      >
                        <Trash2 size={16} />
                        <span className="text-sm">Slet</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lightbox with swipe */}
      {selectedImage && selectedAlbum && (() => {
        const images = selectedAlbum.images || [];
        const currentIdx = images.findIndex(img => img.image_id === selectedImage.image_id);
        const hasPrev = currentIdx > 0;
        const hasNext = currentIdx < images.length - 1;
        const goToPrev = () => { if (hasPrev) setSelectedImage(images[currentIdx - 1]); };
        const goToNext = () => { if (hasNext) setSelectedImage(images[currentIdx + 1]); };
        
        return (
          <div
            className="fixed inset-0 bg-black/98 z-[60] flex items-center justify-center"
            onClick={() => setSelectedImage(null)}
            onTouchStart={(e) => { e.currentTarget._touchX = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const diff = e.changedTouches[0].clientX - (e.currentTarget._touchX || 0);
              if (diff > 60) goToPrev();
              else if (diff < -60) goToNext();
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') goToPrev();
              else if (e.key === 'ArrowRight') goToNext();
              else if (e.key === 'Escape') setSelectedImage(null);
            }}
            tabIndex={0}
            ref={(el) => el && el.focus()}
            data-testid="lightbox-overlay"
          >
            <button
              className="absolute top-6 right-6 text-white text-4xl hover:text-gray-300 z-10"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox-close-btn"
            >
              ×
            </button>
            {/* Counter */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 text-gray-400 text-sm z-10" data-testid="lightbox-counter">
              {currentIdx + 1} / {images.length}
            </div>
            {/* Admin delete button in lightbox */}
            {(member?.role === "admin" || member?.role === "editor") && !selectedAlbum?.isDemo && (
              <button
                className="absolute top-6 left-6 bg-red-600/90 hover:bg-red-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 z-10 transition-all"
                data-testid="lightbox-delete-image-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteImage(selectedAlbum.album_id, selectedImage.image_id);
                  setSelectedImage(null);
                }}
              >
                <Trash2 size={16} />
                Slet billede
              </button>
            )}
            {/* Previous arrow */}
            {hasPrev && (
              <button
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-4xl sm:text-5xl z-10 p-2 transition-colors"
                onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                data-testid="lightbox-prev-btn"
              >
                ‹
              </button>
            )}
            {/* Next arrow */}
            {hasNext && (
              <button
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-4xl sm:text-5xl z-10 p-2 transition-colors"
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                data-testid="lightbox-next-btn"
              >
                ›
              </button>
            )}
            <img
              src={getImageUrl(selectedImage)}
              alt=""
              className="max-w-[85vw] sm:max-w-full max-h-[90vh] object-contain px-10 sm:px-16"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />
          </div>
        );
      })()}

      {/* Album Form Modal */}
      {showAlbumForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="mc-card p-6 w-full max-w-lg">
            <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-6">
              {editingAlbum ? "REDIGER ALBUM" : "TILFØJ BILLEDSERIE"}
            </h2>
            <form onSubmit={handleCreateAlbum} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">Titel *</label>
                <input
                  type="text"
                  value={albumForm.title}
                  onChange={(e) => setAlbumForm({ ...albumForm, title: e.target.value })}
                  className="mc-input w-full"
                  required
                  placeholder="F.eks. Sommertur 2026"
                  data-testid="album-title-input"
                />
              </div>
              
              <div>
                <DatePicker
                  value={albumForm.date}
                  onChange={(v) => setAlbumForm({ ...albumForm, date: v })}
                  label="Dato"
                  required
                  placeholder="Vælg dato"
                  testId="album-date-input"
                />
              </div>
              
              <div>
                <label className="block text-gray-300 mb-2">Beskrivelse</label>
                <textarea
                  value={albumForm.description}
                  onChange={(e) => setAlbumForm({ ...albumForm, description: e.target.value })}
                  className="mc-input w-full h-24 resize-none"
                  placeholder="Kort beskrivelse af album/event..."
                  data-testid="album-description-input"
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button type="submit" disabled={savingAlbum || savedAlbum} className="chrome-btn px-6 py-3 rounded-lg flex-1 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="save-album-btn">
                  {savingAlbum ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Gemmer...</> : savedAlbum ? <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Gemt!</> : editingAlbum ? "Gem ændringer" : "Opret album"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAlbumForm(false);
                    setEditingAlbum(null);
                  }}
                  className="ghost-blue-btn px-6 py-3 rounded-lg"
                >
                  Annuller
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};



export default GalleryPage;
